export type SaveState = "saved" | "dirty" | "saving" | "error"

/** Serializes patches, retaining failed edits and allowing explicit retry. */
export function createSaveQueue<T extends object>(
	save: (patch: T) => Promise<unknown>,
	onState: (state: SaveState) => void,
) {
	let pending: T | null = null
	let running: Promise<void> | null = null
	const flush = (): Promise<void> => {
		if (running) return running.then(() => (pending ? flush() : undefined))
		if (!pending) return Promise.resolve()
		const patch = pending
		pending = null
		onState("saving")
		running = Promise.resolve()
			.then(() => save(patch))
			.then(() => {
				onState(pending ? "dirty" : "saved")
			})
			.catch((error: unknown) => {
				pending = { ...patch, ...pending }
				onState("error")
				throw error
			})
			.finally(() => {
				running = null
			})
		return running.then(() => (pending ? flush() : undefined))
	}
	return {
		schedule(patch: T) {
			pending = { ...pending, ...patch }
			onState("dirty")
		},
		flush,
		get hasPending() {
			return pending !== null || running !== null
		},
	}
}
